import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { CollapsibleOutline, CardViewX, ButtonMenu } from '@ann/web-components';
import { SharedWithMeContainer, FiltersRow, ContentArea, CardViewXContainer } from './ShareWithMe.styled';
import Filters from './components/Filters/Filters';
import { useDrivesStores } from '../../hook/use-drives.hook';
import DocumentCard from '../../components/DocumentCard';
import FolderInformationSlideout from '../../components/FolderInformationSlideout/FolderInformationSlideout';
import {formatLastUpdated} from "../../../../../utils/dateUtils";
import { LoadMoreSection } from '../../components/LoadMoreSection';
import { useLoadMorePagination } from '../../hooks/useLoadMorePagination.js';
import { BannerSelected } from '../../components/BannerSelected';
import { useToastHandler } from '../../hooks/useToastHandler';
import { useBannerHandlers } from '../../hooks/useBannerHandlers';
import ShareModal from '../MyWorkspace/components/Modals/ShareModal';
import AssignAgentModal from '../MyWorkspace/components/Modals/AssignAgentModal';
import DuplicateModal from '../MyWorkspace/components/Modals/DuplicateModal';
import MoveFolderModal from '../../components/MoveFolderModal';
import UnifiedTable from '../../components/UnifiedTable';
import RenameModal from '../../components/RenameModal';
import DocumentDetailSlideout from '../../components/DocumentDetailSlideout/DocumentDetailSlideout';

const CLOSE_DELAY_MS = 170;

const SharedWithMe = observer(() => {
  const { sharedWithMeStore } = useDrivesStores();
  const { executeWithToast, executeSyncWithToast } = useToastHandler(sharedWithMeStore);
  const { handleShare: handleBannerShare, handleMove } = useBannerHandlers(sharedWithMeStore);

  const {
    visibleItems: visibleFolders,
    hasMore: hasMoreFolders,
    isLoading: isLoadingFolders,
    loadMore: loadMoreFolders,
  } = useLoadMorePagination(sharedWithMeStore.filteredFolders, 8, 2000);

  const {
    visibleItems: visibleDocuments,
    hasMore: hasMoreDocuments,
    isLoading: isLoadingDocuments,
    loadMore: loadMoreDocuments,
  } = useLoadMorePagination(sharedWithMeStore.filteredDocuments, 8, 2000);

  useEffect(() => {
    sharedWithMeStore.loadSharedData();
  }, [sharedWithMeStore]);

  const [isFolderInfoOpen, setIsFolderInfoOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const [isDocumentDetailOpen, setIsDocumentDetailOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const handleFolderInfo = useCallback((folder) => {
    if (folder) {
      setSelectedFolder(folder);
      setIsFolderInfoOpen(true);
    }
  }, []);

  const handleShare = useCallback((folder, type) => {
    if (sharedWithMeStore.openShareModal) {
      sharedWithMeStore.openShareModal(folder, type);
    }
  }, [sharedWithMeStore]);

  const handleRenameFolder = useCallback((folder) => {
    if (sharedWithMeStore.openRenameFolderModal) {
      sharedWithMeStore.openRenameFolderModal(folder);
    }
  }, [sharedWithMeStore]);

  const handleRenameDocument = useCallback((document) => {
    if (sharedWithMeStore.openRenameDocumentModal) {
      sharedWithMeStore.openRenameDocumentModal(document);
    }
  }, [sharedWithMeStore]);

  const handleAssignAgent = useCallback((document) => {
    if (sharedWithMeStore.openAssignAgentModal) {
      sharedWithMeStore.openAssignAgentModal(document);
    }
  }, [sharedWithMeStore]);

  const handleDuplicate = useCallback((document) => {
    if (sharedWithMeStore.openDuplicateModal) {
      sharedWithMeStore.openDuplicateModal(document);
    }
  }, [sharedWithMeStore]);

  const handleOpenDocumentDetail = useCallback((document) => {
    setSelectedDocument(document);
    setIsDocumentDetailOpen(true);
  }, []);

  const closeSlideoutThen = useCallback((next) => {
    setIsDocumentDetailOpen(false);
    setTimeout(next, CLOSE_DELAY_MS);
  }, []);

  // Handlers simplificados usando hooks
  const handleAddToPending = () => executeSyncWithToast(sharedWithMeStore.addToPendingGrantees);
  const handleShareAll = () => executeWithToast(sharedWithMeStore.shareAllPendingGrantees);
  const handleRemoveAccess = (granteeUuid, granteeType) => 
    executeWithToast(sharedWithMeStore.removeAccessFromItem, granteeUuid, granteeType);
  const handleUpdatePermission = (granteeUuid, granteeType, newPermission) => 
    executeWithToast(sharedWithMeStore.updatePermissionForGrantee, granteeUuid, granteeType, newPermission);

  const handleAssignAgentAction = () => executeWithToast(sharedWithMeStore.assignAgentsToDocument);

  const handleDuplicateAction = () => executeWithToast(sharedWithMeStore.duplicateItem);

  return (
    <SharedWithMeContainer>
      <RenameModal store={sharedWithMeStore.renameFolderModalStore} />
      <RenameModal store={sharedWithMeStore.renameDocumentModalStore} />
      {sharedWithMeStore.selectionStore.countSelectedDocuments === 0 &&
        sharedWithMeStore.selectionStore.countSelectedFolders === 0 && (
          <FiltersRow>
            <Filters
              onFilterChange={sharedWithMeStore.setFilterText}
              onSortChange={sharedWithMeStore.setSortBy}
              sortBy={sharedWithMeStore.sortBy}
              sortDirection={sharedWithMeStore.sortDirection}
            />
          </FiltersRow>
        )}
      <BannerSelected 
        store={sharedWithMeStore} 
        selectionStore={sharedWithMeStore.selectionStore}
        onShare={handleBannerShare} 
        onMove={handleMove} 
      />
      {sharedWithMeStore.viewMode === 'table' ? (
        <UnifiedTable
          /* folders={visibleFolders} */
          folders={[]}
          documents={visibleDocuments}
          selectionStore={sharedWithMeStore.selectionStore}
          onShare={handleShare}
          onRenameFolder={handleRenameFolder}
          onRenameDocument={handleRenameDocument}
          onDuplicate={handleDuplicate}
          onFolderInfo={handleFolderInfo}
          onMove={(itemId, type) => sharedWithMeStore.openMoveModal(itemId, type)}
        />
      ) : (
        <ContentArea>
        {/* <CollapsibleOutline
          itemCount={visibleFolders?.length || 0}
          title="Folders"
          defaultOpen={true}
        >
          <CollapsibleOutline.Content>
            <CardViewXContainer>
              {visibleFolders && visibleFolders.map((folder) => (
                <CardViewX
                    key={folder?.id || `folder-${Math.random()}`}
                    checked={folder?.isSelected || false}
                    isCheckboxable={folder?.isHovered || false}
                    onCheckboxChange={() => {
                        folder?.setSelected && folder.setSelected(!folder.isSelected);
                        folder?.id && sharedWithMeStore.selectionStore.toggleFolderSelection(folder.id);
                    }}
                    onMouseEnter={() => folder?.setHover && folder.setHover(true)}
                    onMouseLeave={() => folder?.setHover && folder.setHover(false)}
                  showHover
                  size="medium"
                >
                  <CardViewX.Header
                    icon="folder"
                    maxCharacters={90}
                    title={folder?.title || 'Unknown Folder'}
                    menu={
                      <ButtonMenu
                        customIcon="dotsMenu"
                        disableEffects={true}
                        menuPosition="bottom"
                        size="medium"
                        type="tertiary"
                        width="180px"
                        className="dots-menu"
                      >
                        <ButtonMenu.Menu icon={true}>
                          <ButtonMenu.Item
                            label="Rename"
                            value="Rename"
                            icon="edit"
                            onClick={() => folder && sharedWithMeStore.openRenameFolderModal(folder)}
                          >
                            Rename
                          </ButtonMenu.Item>
                          <ButtonMenu.Item
                            label="Share"
                            value="Share"
                            icon="personAdd"
                            onClick={() => folder && handleShare(folder, 'folder')}
                          >
                            Share
                          </ButtonMenu.Item>
                          <ButtonMenu.Item
                            label="Move"
                            value="Move"
                            icon="driveFileMove"
                            onClick={() => folder?.id && sharedWithMeStore.openMoveModal(folder.id, 'folder')}
                          >
                            Move
                          </ButtonMenu.Item>
                          <ButtonMenu.Item
                            label="Folder Information"
                            value="Folder Information"
                            icon="info"
                            onClick={() => folder && handleFolderInfo(folder)}
                          >
                            Folder Information
                          </ButtonMenu.Item>
                        </ButtonMenu.Menu>
                      </ButtonMenu>
                    }
                  />
                  <CardViewX.Meta>
                    <CardViewX.MetaItem
                      icon="lastUpdated"
                      label={formatLastUpdated(folder?.lastUpdated).label}
                      tooltipContent={formatLastUpdated(folder?.lastUpdated).tooltip}
                    />
                    <CardViewX.MetaItem
                      icon="folder"
                      label={folder?.folderCount || '0'}
                      tooltipContent="File size"
                    />
                  </CardViewX.Meta>
                </CardViewX>
              ))}
              <LoadMoreSection
                hasMore={hasMoreFolders}
                isLoading={isLoadingFolders}
                onLoadMore={loadMoreFolders}
                skeletonType="folder"
              />
            </CardViewXContainer>
          </CollapsibleOutline.Content>
        </CollapsibleOutline> */}
        <CollapsibleOutline
          itemCount={visibleDocuments.length}
          title="Documents"
          defaultOpen={true}
        >
          <CollapsibleOutline.Content>
            <CardViewXContainer>
              {visibleDocuments.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  id={doc.id}
                  title={doc.title}
                  owner={doc.owner}
                  date={doc.date}
                  status={doc.status}
                  tags={doc.tags}
                  checked={doc.isSelected}
                  isCheckboxable={doc.isHovered}
                  onCheckboxChange={() => {
                      doc.setSelected(!doc.isSelected);
                      sharedWithMeStore.selectionStore.toggleDocumentSelection(doc.id);
                  }}
                  onRename={() => doc && handleRenameDocument(doc)}
                  onShare={() => handleShare(doc, 'document')}
                  onAssignAgent={() => doc && handleAssignAgent(doc)}
                  onDuplicate={() => doc && handleDuplicate(doc)}
                  onMove={() => doc?.id && sharedWithMeStore.openMoveModal(doc.id, 'document')}
                  onMouseEnter={() => doc.setHover(true)}
                  onMouseLeave={() => doc.setHover(false)}
                  showHover
                  size="medium"
                  isUploading={doc.isUploading}
                  uploadProgress={doc.uploadProgress}
                  onOpenDocumentDetail={() => handleOpenDocumentDetail(doc)}
                />
              ))}
              <LoadMoreSection
                hasMore={hasMoreDocuments}
                isLoading={isLoadingDocuments}
                onLoadMore={loadMoreDocuments}
                skeletonType="document"
              />
            </CardViewXContainer>
          </CollapsibleOutline.Content>
        </CollapsibleOutline>
        </ContentArea>
      )}

      <FolderInformationSlideout
        open={isFolderInfoOpen}
        onClose={() => setIsFolderInfoOpen(false)}
        folderName={selectedFolder?.title || selectedFolder?.name || 'Unknown Folder'}
        ownerName={selectedFolder?.owner_full_name || selectedFolder?.owner || 'Unknown'}
      />
      <ShareModal
        isOpen={sharedWithMeStore.isShareModalOpen}
        store={sharedWithMeStore}
        resourcesStore={sharedWithMeStore.resourcesStore}
        onClose={() => sharedWithMeStore.closeShareModal()}
        onShare={handleShareAll}
        onAddToPending={handleAddToPending}
        onUpdatePendingPermission={sharedWithMeStore.updatePendingGranteePermission}
        onRemovePending={sharedWithMeStore.removePendingGrantee}
        onUpdateGranteePermission={handleUpdatePermission}
        onRemoveGrantee={handleRemoveAccess}
      />
      <MoveFolderModal
        isOpen={sharedWithMeStore.isMoveModalOpen}
        onClose={() => sharedWithMeStore.closeMoveModal()}
        onSave={() => {
          sharedWithMeStore.closeMoveModal();
          executeSyncWithToast(() => ({
            success: true,
            title: 'Success',
            message: `${
              sharedWithMeStore.itemTypeBeingMoved === "folder"
                ? "Folder"
                : "Document"
            } moved successfully.`,
            variant: 'success'
          }));
        }}
        isSubmitting={false}
      />

      <DocumentDetailSlideout
        open={isDocumentDetailOpen}
        onClose={() => setIsDocumentDetailOpen(false)}
        document={selectedDocument}
        showActions
        onRename={(document) => {
          closeSlideoutThen(() => {
            handleRenameDocument(document);
          });
        }}
        onShare={(item, type) => {
          closeSlideoutThen(() => {
            handleShare(item, type);
          });
        }}
        onManageAccess={(doc) => {
          closeSlideoutThen(() => {
            handleShare(doc, 'document');
          });
        }}
      />

      <AssignAgentModal
        isOpen={sharedWithMeStore.isAssignAgentModalOpen}
        store={sharedWithMeStore}
        onClose={() => sharedWithMeStore.closeAssignAgentModal()}
        onConfirm={handleAssignAgentAction}
      />

      <DuplicateModal
        isOpen={sharedWithMeStore.isDuplicateModalOpen}
        store={sharedWithMeStore}
        onClose={() => sharedWithMeStore.closeDuplicateModal()}
        onConfirm={handleDuplicateAction}
      />
    </SharedWithMeContainer>
  );
});

export default SharedWithMe;